import { createUserWithEmailAndPassword, getAuth, signInWithCustomToken, signInWithEmailAndPassword, updateProfile, signOut } from 'firebase/auth'

// //////////////////////////////////////////////////////////////////
// 💬 Create Application User
// //////////////////////////////////////////////////////////////////
// Create User
export const createUser = async (email: string, password: string) => {
  const auth = getAuth()
  try {
    return await createUserWithEmailAndPassword(auth, email, password)
  }
  catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    return `Failed Creating User: ${email} | ${errorCode} ${errorMessage}`
  }
}

// //////////////////////////////////////////////////////////////////
// 💬 Logout User
// //////////////////////////////////////////////////////////////////
// User Logout
export const userLogout = async () => {
  const { $user } = useNuxtApp()
  const router = useRouter()
  const auth = getAuth()

  try {

    // 💬 Clear Local Cookies and Storage
    const userCookie = useCookie('user', { maxAge: 180 * 60 })
    const snowflakeAuth = useGetLocalStorage('snowflakeAuth')
    userCookie.value = null
    if (snowflakeAuth) useSetLocalStorage('snowflakeAuth', JSON.stringify({}))

    const authStore = useAuthStore()
    authStore.clearUser()

    // 💬 Firebase Sign Out
    await signOut(auth)

    // 💬 Reroute to Login Page
    router.push({ path: '/login' })

    // 💬 Unsub From Docs and Remove Doc Subscriptions
    const { docs } = storeToRefs(useDocStore())
    if (docs.value) {
      const keys = Object.keys(docs.value)
      keys.forEach((key) => {
        const doc = docs.value[key]
        if (doc?.unsub && typeof doc.unsub === 'function')
          doc.unsub()
        delete docs.value[key]
      })
    }

    // 💬 Clear Sensitive Data Stores
    const { snowflakeCredentials } = storeToRefs(useSnowflakeAuthStore())
    snowflakeCredentials.value = {} as any
  }
  catch (error: any) {
    console.error('userLogout error:', error)
    const errorCode = error.code
    const errorMessage = error.message

    return `Failed To Logout User | ${errorCode} ${errorMessage}`
  }
}

// //////////////////////////////////////////////////////////////////
// 💬 User Login - Password or Custom Token Authentication
// //////////////////////////////////////////////////////////////////
export const userLogin = async (email: string, password: string | null, customToken: string | null, isSnowflakeAuth: boolean = false): Promise<any | string> => {
  const { snowflakeCredentials } = storeToRefs(useSnowflakeAuthStore())
  const auth = getAuth()
  const router = useRouter()
  try {
    const loginUser = password ? await signInWithEmailAndPassword(auth, email, password) : customToken ? await signInWithCustomToken(auth, customToken) : null
    if (!loginUser)
      return null

    // 💬 Get Account Details From Firebase
    // Note that the field "displayName" holds the users account ID
    const user = ref({} as any)
    user.value = loginUser.user
    const userId = user.value.uid

    // 💬 Set If Snowflake Is Authenticating The Application
    // If Snowflake is Unavailable User Is Logged Out
    snowflakeCredentials.value.appAuthenticatedWithSnowflake = isSnowflakeAuth

    // 💬 Update Snowflake Credentials on Firestore
    if (snowflakeCredentials.value.refreshTokenEncrypted) {
      useSetDoc('users', userId, { snowflakeConnections: { 1: { refreshToken: snowflakeCredentials.value.refreshTokenEncrypted, refreshTokenExpiresAt: snowflakeCredentials.value.refreshTokenExpiresAt } } })
    }

    // 💬 Write Snowflake Authentication to LocalStorage
    // For Persistence On Browser Refresh
    // /////////////////////////////////////////////////
    useSetLocalStorage('snowflakeAuth', JSON.stringify(snowflakeCredentials.value))

    // 💬 Sync Profile and Account
    const globalsStore = useGlobalsStore()
    const { userId: globalUserId, accountId: globalAccountId } = storeToRefs(globalsStore)

    globalUserId.value = userId
    if (loginUser.user.displayName) {
      globalAccountId.value = loginUser.user.displayName
      globalsStore.syncAccountProfile(loginUser.user.displayName)
      globalsStore.syncUserProfile(loginUser.user.displayName, userId)
    }

    // 💬 Ensure cookie and store are set before we push route
    const userCookie = useCookie('user', { maxAge: 180 * 60 })
    userCookie.value = JSON.stringify({ uid: loginUser.user.uid, email: loginUser.user.email })

    // Set user explicitly in store
    const authStore = useAuthStore()
    authStore.setUser(loginUser.user)

    // 💬 Reroute to Main Page
    router.push({ path: '/' })

    return loginUser.user
  }
  catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    return { error: `Failed To Login In User: ${email} | ${errorCode} ${errorMessage}` }
  }
}

// //////////////////////////////////////////////////////////////////
// 💬 Update Firebase Auth
// //////////////////////////////////////////////////////////////////
export const updateFirebaseUser = async (payload: any) => {
  const auth = getAuth()
  try {
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, payload)
      console.log('User Was Updated')
    }
    else {
      throw new Error('No authenticated user found')
    }
  }
  catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    return `Failed To Update User | ${errorCode} ${errorMessage}`
  }
}

// //////////////////////////////////////////////////////////////////
// 💬 Getting Custom Token Claims
// //////////////////////////////////////////////////////////////////
export const getUsersCustomTokenClaims = async () => {
  console.log('Getting Custom Claims For User')

  const { $auth } = useNuxtApp()
  const user = $auth.currentUser
  const tokenResults = user ? await user.getIdTokenResult() : null

  return tokenResults ? tokenResults.claims : {}
}
